import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Printer, X } from 'lucide-react';
import proformaInvoiceService from '../../../services/proformaInvoiceService';
import companyService from '../../../services/companyService';
import { toast } from 'react-toastify';
import celerisSign from '../../../assets/celeris_sign.png';
import './TaxInvoicePrint.css';

const ProformaInvoicePrint = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  // Add print page setup for A4 size and no browser headers/footers
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'print-page-style';
    style.textContent = `
      @media print {
        @page {
          size: A4;
          margin: 0;
        }
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
        html {
          margin: 0 !important;
          padding: 0 !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      const existingStyle = document.getElementById('print-page-style');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [invoiceResponse, companyResponse] = await Promise.all([
        proformaInvoiceService.getProformaInvoiceById(id),
        companyService.getCompanyDetails()
      ]);

      if (invoiceResponse.success && invoiceResponse.data) {
        setInvoice(invoiceResponse.data);
      } else {
        toast.error('Failed to load invoice data');
      }

      if (companyResponse.success && companyResponse.data) {
        setCompany(companyResponse.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load invoice data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const numberToWords = (num) => {
    // Simple number to words converter (can be enhanced)
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    if (num === 0) return 'Zero';
    if (num < 10) return ones[num];
    if (num < 20) return teens[num - 10];
    if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + ones[num % 10] : '');
    if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 !== 0 ? ' ' + numberToWords(num % 100) : '');
    if (num < 100000) return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 !== 0 ? ' ' + numberToWords(num % 1000) : '');
    if (num < 10000000) return numberToWords(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 !== 0 ? ' ' + numberToWords(num % 100000) : '');
    return numberToWords(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 !== 0 ? ' ' + numberToWords(num % 10000000) : '');
  };

  const handlePrint = () => {
    // Wait a bit for any rendering to complete
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleClose = () => {
    window.close();
  };

  if (loading) {
    return (
      <div className="invoice-print-container">
        <div className="invoice-loading">Loading invoice...</div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="invoice-print-container">
        <div className="invoice-error">Invoice not found</div>
      </div>
    );
  }

  const companyAddress = company?.companyBillingAddress;
  const companyContact = company?.contactInfo?.[0];
  const customer = invoice.customer;
  const customerBillingAddress = customer?.customerBillingAddress?.[0];
  const customerShippingAddress = customer?.customerShippingAddress?.[0] || customerBillingAddress;
  
  // Determine GST type from invoice's gstType field (set by backend GST calculation logic)
  // gstType can be: 'CGST_SGST', 'CGST_UTGST', 'IGST', 'ZERO_RATED'
  const gstType = invoice.gstType || (invoice.totalIGSTAmount > 0 ? 'IGST' : 'CGST_SGST');
  const isInterState = gstType === 'IGST';
  const isIntraState = gstType === 'CGST_SGST' || gstType === 'CGST_UTGST';
  const isUnionTerritory = gstType === 'CGST_UTGST';
  const isZeroRated = gstType === 'ZERO_RATED';
  
  // Get place of supply from invoice or calculate
  const placeOfSupply = invoice.placeOfSupply || customerBillingAddress?.state || companyAddress?.state || '';
  
  // Prepare service details: always show exactly 5 rows
  const originalServiceDetails = invoice.serviceDetails || [];
  const hasMoreThanFiveItems = originalServiceDetails.length > 5;
  const displayServiceDetails = (() => {
    const items = originalServiceDetails.slice(0, 5);
    while (items.length < 5) {
      items.push({});
    }
    return items;
  })();

  // Parse paymentTerms (terms and conditions) - can be string with newlines or array
  const termsAndConditions = invoice.paymentTerms 
    ? (typeof invoice.paymentTerms === 'string' && invoice.paymentTerms.includes('\n')
        ? invoice.paymentTerms.split('\n').filter(t => t.trim())
        : [invoice.paymentTerms].filter(t => t.trim()))
    : [];

  return (
    <div className="invoice-print-container">
      {/* Print Controls - Hidden when printing */}
      <div className="invoice-print-controls no-print">
        <button onClick={handlePrint} className="btn-print">
          <Printer size={16} />
          Print
        </button>
        <button onClick={handleClose} className="btn-close">
          <X size={16} />
        </button>
      </div>

      {/* Invoice Content */}
      <div className="invoice-content">
        {/* Compact Header */}
        <div className="invoice-header-compact">
          <div className="invoice-company-info">
            <div className="invoice-company-name-compact">
              {company?.companyName?.toUpperCase() || '-'}
            </div>
            <div className="invoice-company-address-compact">
              {companyAddress ? (
                (() => {
                  const addressParts = [
                    companyAddress.addressLine1,
                    companyAddress.addressLine2,
                    companyAddress.addressLine3,
                    companyAddress.addressLine4,
                    `${companyAddress.city || ''}${companyAddress.city && companyAddress.state ? ', ' : ''}${companyAddress.state || ''} ${companyAddress.pinCode || ''}`.trim(),
                    companyAddress.country || 'India'
                  ].filter(Boolean);
                  return addressParts.length > 0 ? addressParts.join(', ') : '-';
                })()
              ) : '-'}
            </div>
            <div className="invoice-company-contact-compact">
              <span>Phone: +91 89289 81749</span>
              <span>Email: nilesh@celerisventures.ai</span>
            </div>
            <div className="invoice-company-ids-compact">
              <span>CIN: {company?.companyCIN || '-'}</span>
              <span>GSTIN: {company?.GSTIN || '-'}</span>
            </div>
          </div>
          <div className="invoice-title-compact">Proforma Invoice</div>
        </div>

        {/* Three Column Layout: Buyer, Consignee, Document Details */}
        <div className="invoice-details-three-column">
          {/* Buyer's Details [Bill To] */}
          <div className="invoice-detail-column">
            <div className="invoice-column-title">Buyer's Details [Bill To]</div>
            <div className="invoice-column-content">
              <div className="invoice-detail-field">
                <strong>Name:</strong> {invoice.customerName || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>Address:</strong> {customerBillingAddress ? (
                  (() => {
                    const addressParts = [
                      customerBillingAddress.line1,
                      customerBillingAddress.line2,
                      customerBillingAddress.line3,
                      customerBillingAddress.line4,
                      `${customerBillingAddress.city || ''}${customerBillingAddress.city && customerBillingAddress.state ? ', ' : ''}${customerBillingAddress.state || ''} ${customerBillingAddress.pinCode || ''}`.trim(),
                      customerBillingAddress.country || 'India'
                    ].filter(Boolean);
                    return addressParts.length > 0 ? addressParts.join(', ') : '-';
                  })()
                ) : '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>GSTIN:</strong> {invoice.customerGSTIN || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>Mobile No.:</strong> {customer?.customerContactInfo?.[0]?.contactPersonNumber || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>E-mail:</strong> {customer?.customerContactInfo?.[0]?.contactPersonEmail || '-'}
              </div>
            </div>
          </div>

          {/* Consignee Details [Ship To] */}
          <div className="invoice-detail-column">
            <div className="invoice-column-title">Consignee Details [Ship To]</div>
            <div className="invoice-column-content">
              <div className="invoice-detail-field">
                <strong>Name:</strong> {customerShippingAddress ? (invoice.customerName || '-') : '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>Address:</strong> {customerShippingAddress ? (
                  (() => {
                    const addressParts = [
                      customerShippingAddress.line1,
                      customerShippingAddress.line2,
                      customerShippingAddress.line3,
                      customerShippingAddress.line4,
                      `${customerShippingAddress.city || ''}${customerShippingAddress.city && customerShippingAddress.state ? ', ' : ''}${customerShippingAddress.state || ''} ${customerShippingAddress.pinCode || ''}`.trim(),
                      customerShippingAddress.country || 'India'
                    ].filter(Boolean);
                    return addressParts.length > 0 ? addressParts.join(', ') : '-';
                  })()
                ) : '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>GSTIN:</strong> {invoice.customerGSTIN || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>Mobile No.:</strong> {customerShippingAddress?.contactPersonNumber || customer?.customerContactInfo?.[0]?.contactPersonNumber || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>E-mail:</strong> {customer?.customerContactInfo?.[0]?.contactPersonEmail || '-'}
              </div>
            </div>
          </div>

          {/* Document Details */}
          <div className="invoice-detail-column">
            <div className="invoice-column-title">Document Details</div>
            <div className="invoice-column-content">
              <div className="invoice-detail-field">
                <strong>Invoice No.:</strong> {invoice.proformaInvoiceNumber || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>Invoice Date:</strong> {formatDate(invoice.proformaInvoiceDate) || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>Valid Until:</strong> {formatDate(invoice.validUntil) || '-'}
              </div>
              <div className="invoice-detail-field">
                <strong>Supply Type Code:</strong> {invoice.customerCategory === 'Export' || invoice.customerCategory === 'SEZ' || invoice.customerCategory === 'Deemed Export' ? 'B2B Export' : 'B2B'}
              </div>
              <div className="invoice-detail-field">
                <strong>Place of Supply:</strong> {placeOfSupply} {(() => {
                  // Extract state code from customer GSTIN or company GSTIN
                  let stateCode = '';
                  if (invoice.customerGSTIN && invoice.customerGSTIN.length >= 2) {
                    stateCode = invoice.customerGSTIN.substring(0, 2);
                  } else if (company?.GSTIN && company.GSTIN.length >= 2) {
                    stateCode = company.GSTIN.substring(0, 2);
                  }
                  return stateCode ? `[${stateCode}]` : '';
                })()}
              </div>
              <div className="invoice-detail-field">
                <strong>Currency:</strong> {invoice.currency || 'INR'}
              </div>
            </div>
          </div>
        </div>

        {/* Service Details Table */}
        <div className="invoice-services-section">
          <div className="invoice-section-title">Description of Services</div>
          <table className="invoice-services-table">
            <thead>
              <tr>
                <th>Sr No.</th>
                <th>Description of Services</th>
                <th>HSN/SAC</th>
                <th>Quantity</th>
                <th>Unit</th>
                <th>Rate (INR)</th>
                {isInterState ? (
                  <th>IGST (%)</th>
                ) : isUnionTerritory ? (
                  <>
                    <th>CGST (%)</th>
                    <th>UTGST (%)</th>
                  </>
                ) : (
                  <>
                    <th>CGST (%)</th>
                    <th>SGST (%)</th>
                  </>
                )}
                <th>Total Amount (INR)</th>
              </tr>
            </thead>
            <tbody>
              {displayServiceDetails.map((service, index) => {
                const isEmpty = !service.serviceDescription && !service.service?.serviceDescription;
                return (
                  <tr key={index} className={isEmpty ? 'empty-row' : ''}>
                    <td>{isEmpty ? '' : index + 1}</td>
                    <td>
                      {isEmpty ? '' : (
                        <>
                          {service.serviceDescription || service.service?.serviceDescription || ''}
                          {service.additionalInfo && <div className="service-reference">Reference: {service.additionalInfo}</div>}
                        </>
                      )}
                    </td>
                    <td>{isEmpty ? '' : (service.SACCode || service.service?.SACCode || '')}</td>
                    <td>{isEmpty ? '' : (service.qty || 0)}</td>
                    <td>{isEmpty ? '' : (service.unit || 'Nos')}</td>
                    <td>{isEmpty ? '' : formatCurrency(service.unitRate || 0)}</td>
                    {isInterState ? (
                      <td>
                        {isEmpty ? '' : formatCurrency(service.igstAmount || 0)}
                      </td>
                    ) : isUnionTerritory ? (
                      <>
                        <td>
                          {isEmpty ? '' : formatCurrency(service.cgstAmount || 0)}
                        </td>
                        <td>
                          {isEmpty ? '' : formatCurrency(service.utgstAmount || 0)}
                        </td>
                      </>
                    ) : (
                      <>
                        <td>
                          {isEmpty ? '' : formatCurrency(service.cgstAmount || 0)}
                        </td>
                        <td>
                          {isEmpty ? '' : formatCurrency(service.sgstAmount || 0)}
                        </td>
                      </>
                    )}
                    <td>{isEmpty ? '' : formatCurrency(service.lineValue || 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Financial Summary */}
        <div className="invoice-summary">
          <div className="invoice-summary-row">
            <span className="invoice-summary-label">Total Amount (INR):</span>
            <span className="invoice-summary-value">₹ {formatCurrency(invoice.totalValue || 0)}</span>
          </div>
          {isZeroRated ? (
            <div className="invoice-summary-row">
              <span className="invoice-summary-label">GST (Zero Rated):</span>
              <span className="invoice-summary-value">₹ {formatCurrency(0)}</span>
            </div>
          ) : isInterState ? (
            <div className="invoice-summary-row">
              <span className="invoice-summary-label">IGST @ {invoice.serviceDetails?.[0]?.igstRate || invoice.GSTDetails?.[0]?.igstRate || 18}%:</span>
              <span className="invoice-summary-value">₹ {formatCurrency(invoice.totalIGSTAmount || 0)}</span>
            </div>
          ) : isUnionTerritory ? (
            <>
              <div className="invoice-summary-row">
                <span className="invoice-summary-label">CGST @ {invoice.serviceDetails?.[0]?.cgstRate || invoice.GSTDetails?.[0]?.cgstRate || 9}%:</span>
                <span className="invoice-summary-value">₹ {formatCurrency(invoice.totalCGSTAmount || 0)}</span>
              </div>
              <div className="invoice-summary-row">
                <span className="invoice-summary-label">UTGST @ {invoice.serviceDetails?.[0]?.utgstRate || invoice.GSTDetails?.[0]?.utgstRate || 9}%:</span>
                <span className="invoice-summary-value">₹ {formatCurrency(invoice.totalUTGSTAmount || 0)}</span>
              </div>
            </>
          ) : (
            <>
              <div className="invoice-summary-row">
                <span className="invoice-summary-label">CGST @ {invoice.serviceDetails?.[0]?.cgstRate || invoice.GSTDetails?.[0]?.cgstRate || 9}%:</span>
                <span className="invoice-summary-value">₹ {formatCurrency(invoice.totalCGSTAmount || 0)}</span>
              </div>
              <div className="invoice-summary-row">
                <span className="invoice-summary-label">SGST @ {invoice.serviceDetails?.[0]?.sgstRate || invoice.GSTDetails?.[0]?.sgstRate || 9}%:</span>
                <span className="invoice-summary-value">₹ {formatCurrency(invoice.totalSGSTAmount || 0)}</span>
              </div>
            </>
          )}
          {Math.abs(invoice.roundOffAmount || 0) >= 0.001 && (
            <div className="invoice-summary-row">
              <span className="invoice-summary-label">Round Off:</span>
              <span className="invoice-summary-value">₹ {formatCurrency(invoice.roundOffAmount || 0)}</span>
            </div>
          )}
          <div className="invoice-summary-row invoice-summary-total">
            <span className="invoice-summary-label">Total Invoice Amount:</span>
            <span className="invoice-summary-value">₹ {formatCurrency(invoice.totalAmountWithTax || 0)}</span>
          </div>
          <div className="invoice-amount-words-container">
            <div className="invoice-amount-words">
              Total Invoice Amount (In words): {invoice.currency || 'INR'} {numberToWords(Math.round(invoice.totalAmountWithTax || 0))} Rupees Only
            </div>
            <div className="invoice-payment-terms">
              <div className="invoice-payment-terms-label">Payment Terms</div>
              <div className="invoice-payment-terms-value">{invoice.customerPaymentTerms || '-'}</div>
            </div>
          </div>
        </div>

        {/* Terms & Conditions Section */}
        {termsAndConditions.length > 0 && (
          <div className="invoice-terms-section">
            <div className="invoice-section-title">Terms & Conditions</div>
            <ul className="invoice-terms-list">
              {termsAndConditions.map((term, index) => (
                <li key={index}>{term}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Spacer for print layout */}
        <div className="invoice-print-spacer"></div>

        {/* Bank Details and Authorized Signatory */}
        {company && (
          <div className={`invoice-bank-details ${hasMoreThanFiveItems ? 'page-break-before' : ''}`}>
            <div className="invoice-bank-signatory-container">
              <div className="invoice-bank-section">
                <div className="invoice-section-title">Bank Details:</div>
                <div className="invoice-bank-info">
                  <div><strong>Beneficiary:</strong> {company?.companyName || '-'}</div>
                  <div><strong>Bank Name:</strong> {company?.companyBankName || '-'}</div>
                  <div><strong>Branch:</strong> {company?.companyBankBranch || '-'}</div>
                  <div><strong>Account No.:</strong> {company?.companyAccountNumber || '-'}</div>
                  <div><strong>IFS Code:</strong> {company?.companyBankIFSCCode || '-'}</div>
                </div>
              </div>
              <div className="invoice-signatory-section">
                <div className="invoice-signatory-for">For {company?.companyName || 'Company'}</div>
                <img src={celerisSign} alt="Signature" style={{ marginRight: '25px', maxWidth: '150px', maxHeight: '80px', objectFit: 'contain', display: 'block' }} />
                <div className="invoice-signatory-label">Authorized Signatory</div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className={`invoice-footer ${hasMoreThanFiveItems ? 'page-break-before' : ''}`}>
          <div className="invoice-footer-declaration">
            <div className="invoice-footer-declaration-title">Declaration:</div>
            <div className="invoice-footer-disclaimer">
              This is a Proforma Invoice only and not a Tax/GST Invoice. No GST input credit can be claimed on this document. A final Tax Invoice will be issued upon receipt of PO/confirmation/advance payment.
            </div>
          </div>
          <div className="invoice-footer-computer-generated">
            This is computer-generated invoice no signature required
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProformaInvoicePrint;

